import React, { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
  HiOutlineXCircle,
  HiOutlineX,
} from "react-icons/hi";

// Toast Context
const ToastContext = createContext(null);

// Toast types configuration
const toastConfig = {
  success: {
    icon: HiOutlineCheckCircle,
    bgClass: "from-green-500/20 to-emerald-600/10",
    borderClass: "border-green-500/30",
    iconClass: "text-green-400",
    glowClass: "shadow-[0_0_20px_rgba(34,197,94,0.15)]",
    title: "Mission_Success",
  },
  error: {
    icon: HiOutlineXCircle,
    bgClass: "from-red-500/20 to-rose-600/10",
    borderClass: "border-red-500/30",
    iconClass: "text-red-400",
    glowClass: "shadow-[0_0_20px_rgba(239,68,68,0.15)]",
    title: "System_Error",
  },
  warning: {
    icon: HiOutlineExclamationCircle,
    bgClass: "from-amber-500/20 to-yellow-600/10",
    borderClass: "border-amber-500/30",
    iconClass: "text-amber-400",
    glowClass: "shadow-[0_0_20px_rgba(245,158,11,0.15)]",
    title: "Alert_Protocol",
  },
  info: {
    icon: HiOutlineInformationCircle,
    bgClass: "from-cyan-500/20 to-blue-600/10",
    borderClass: "border-cyan-500/30",
    iconClass: "text-cyan-400",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
    title: "Intel_Update",
  },
};

// Individual Toast Component
const ToastItem = ({ id, type, message, onClose }) => {
  const config = toastConfig[type] || toastConfig.info;
  const Icon = config.icon;

  return (
    <div
      className={`
        relative flex items-start gap-4 p-5 rounded-2xl
        bg-gradient-to-r ${config.bgClass}
        backdrop-blur-2xl border ${config.borderClass}
        ${config.glowClass}
        animate-slideInRight
        min-w-[320px] max-w-[420px]
        group
      `}
      role="alert"
    >
      {/* Cyber accent line */}
      <div className={`absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent ${config.iconClass.replace('text-', 'via-')}/50 to-transparent`} />
      
      {/* Icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center ${config.iconClass}`}>
        <Icon className="text-2xl" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-1">
        <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.3em] mb-1 italic">
          {config.title}
        </p>
        <p className="text-sm font-bold text-white leading-relaxed">
          {message}
        </p>
      </div>

      {/* Close button */}
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-gray-500 hover:text-white transition-all opacity-0 group-hover:opacity-100"
      >
        <HiOutlineX className="text-lg" />
      </button>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl overflow-hidden bg-white/5">
        <div 
          className={`h-full ${config.iconClass.replace('text-', 'bg-')} animate-shrink`}
          style={{ animationDuration: '4000ms' }}
        />
      </div>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed top-6 right-6 z-[99999] flex flex-col gap-4 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem
            id={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={removeToast}
          />
        </div>
      ))}
    </div>,
    document.body
  );
};

// Toast Provider
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((type, message, duration = 4000) => {
    const id = Date.now() + Math.random();
    
    setToasts((prev) => [...prev, { id, type, message }]);

    // Auto remove after duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = {
    success: (message, duration) => addToast("success", message, duration),
    error: (message, duration) => addToast("error", message, duration),
    warning: (message, duration) => addToast("warning", message, duration),
    info: (message, duration) => addToast("info", message, duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// For backwards compatibility - direct toast object
// This creates a simple fallback for files that import { toast } directly
let globalToast = {
  success: (msg) => console.log("Toast success:", msg),
  error: (msg) => console.log("Toast error:", msg),
  warning: (msg) => console.log("Toast warning:", msg),
  info: (msg) => console.log("Toast info:", msg),
};

export const setGlobalToast = (toastFn) => {
  globalToast = toastFn;
};

export const toast = {
  success: (msg, opts) => globalToast.success(msg, opts),
  error: (msg, opts) => globalToast.error(msg, opts),
  warning: (msg, opts) => globalToast.warning(msg, opts),
  info: (msg, opts) => globalToast.info(msg, opts),
};

export default ToastProvider;
